import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../../../interceptors";
import { useTheme } from "../../../context/theme";
import CreateJobWizard from "../../../components/create-job-wizard/CreateJobWizard";
import Interviews from "../interviews-redesign";
import {
  toggleJob,
  setScope,
  selectScopedJobIds,
} from "../../../redux/slices/job-scope-slice";

// The backend serializes List<String> fields by JSON.stringify-ing the array
// once and then splitting it on commas, so a 3-item list arrives as
//   ["[\"Bengaluru\"", "\"Delhi NCR\"", "\"Haldwani\"]"]
// rejoin → JSON.parse → real array. Falls back to literal-strip on failure.
const parseStringList = (val) => {
  if (val == null) return [];
  if (Array.isArray(val) && val.length === 0) return [];
  try {
    const joined = Array.isArray(val) ? val.join(",") : String(val);
    const parsed = JSON.parse(joined);
    if (Array.isArray(parsed))
      return parsed.filter((s) => s != null && s !== "");
    if (typeof parsed === "string") return parsed ? [parsed] : [];
    return [];
  } catch {
    const arr = Array.isArray(val) ? val : [val];
    return arr
      .map((s) => String(s).replace(/^\s*\[?"?|"?\]?\s*$/g, ""))
      .filter(Boolean);
  }
};

/* ── animation variants ── */
const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0 } },
};
const cardVariant = {
  hidden: { opacity: 1, y: 0 },
  show: { opacity: 1, y: 0 },
};
const overlayVariant = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};
const panelVariant = {
  hidden: { opacity: 1, scale: 1, y: 0 },
  visible: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.97, y: 10, transition: { duration: 0.15 } },
};

/* ── Main component ── */
const Jobs = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const scopedIds = useSelector(selectScopedJobIds);
  const { isDark } = useTheme();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pipelineJob, setPipelineJob] = useState(null);
  const [pipelineData, setPipelineData] = useState(null);
  const [digestJobId, setDigestJobId] = useState(null);
  const [toast, setToast] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  // Seed for the wizard form when launched from "+ New via AI". Null = blank
  // (manual flow); object = prefill from /ai/jobs/from-prompt response.
  const [wizardInitialForm, setWizardInitialForm] = useState(null);
  // AI prompt modal — replaces the old navigate("/admin/assistant") shortcut.
  const [aiPromptOpen, setAiPromptOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [trackerJob, setTrackerJob] = useState(null);
  const [trackerData, setTrackerData] = useState(null);
  const [detailJob, setDetailJob] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  // JD preview state: { job, url, loading, error, contentDisposition }
  const [jdPreview, setJdPreview] = useState(null);

  // Close menu on outside click
  useEffect(() => {
    if (!openMenuId) return;
    const close = () => setOpenMenuId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [openMenuId]);

  // Filter & pagination
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    q: "",
    status: "",
    location: "",
    skill: "",
    employmentType: "",
    minYears: "",
    maxYears: "",
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(6);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // Admins see all org jobs; regular members (Organization_User) see only the
  // jobs they created. The list is already scoped server-side from the JWT — this
  // flag only tailors the copy (banner / empty state). Default true (show all)
  // so a resolution hiccup never mislabels an admin's screen.
  const [isAdmin, setIsAdmin] = useState(true);
  const activeFilterCount = Object.values(filters).filter(
    (v) => v !== "",
  ).length;

  const load = async (filterOverride, pageOverride) => {
    setLoading(true);
    try {
      const f = filterOverride || filters;
      const pg = pageOverride !== undefined ? pageOverride : currentPage;
      const userId = localStorage.getItem("userId") || "1";

      const r = await axiosInstance.post(
        "/job-posting/job-details/get-all-published-jobs",
        {
          userId,
          start: String(pg * pageSize),
          length: String(pageSize),
        },
      );
      const rawList = r.data?.list || [];
      // Map existing API fields to what the UI expects
      let mapped = rawList.map((j) => ({
        id: j.jobId,
        title: j.positionName,
        status: j.jobOpenStatus === false ? "CLOSED" : "OPEN",
        location: parseStringList(j.locations).join(", "),
        openings: j.positionCounts,
        hiringType: j.hiringType,
        minExperience: j.minimumExperience,
        maxExperience: j.maximumExperience,
        minCtc: j.minCtc,
        maxCtc: j.maxCtc,
        interviewRounds: parseStringList(j.interviewRounds),
        createdAt: j.vacancyStartDate || j.lastModifiedOn || null,
        skills: parseStringList(j.domainSkills)
          .map((s) => (typeof s === "string" ? s : s?.name || ""))
          .filter(Boolean),
        // Real stats from /job-posting/job-details/get-all-published-jobs
        invitedCount: j.invitedCount,
        inProgressCount: j.inProgressCount,
        completedCount: j.completedCount,
        scoredCount: j.scoredCount,
        awaitingCount: j.awaitingCount,
        averageScore: j.averageScore,
        // Digest config (additive fields on JobDetailsDTO)
        digestEnabled: !!j.digestEnabled,
        digestRecipients: Array.isArray(j.digestRecipients)
          ? j.digestRecipients
          : [],
        digestLastSentAt: j.digestLastSentAt || null,
      }));
      // Client-side filtering
      if (f.q) {
        const q = f.q.toLowerCase();
        mapped = mapped.filter(
          (j) =>
            j.title?.toLowerCase().includes(q) ||
            j.location?.toLowerCase().includes(q),
        );
      }
      if (f.status) mapped = mapped.filter((j) => j.status === f.status);
      if (f.location)
        mapped = mapped.filter((j) =>
          j.location?.toLowerCase().includes(f.location.toLowerCase()),
        );

      setJobs(mapped);
      setTotalElements(r.data?.recordsTotal || mapped.length);
      setTotalPages(
        Math.ceil((r.data?.recordsTotal || mapped.length) / pageSize),
      );
      setCurrentPage(pg);
    } catch (err) {
      console.error("Failed to load jobs:", err);
      // Fallback: try get-all-jobs
      try {
        const userId = localStorage.getItem("userId") || "1";
        const r = await axiosInstance.post(
          "/job-posting/job-details/get-all-jobs",
          { userId, start: "0", length: "50" },
        );
        const mapped = (r.data?.list || []).map((j) => ({
          id: j.jobId,
          title: j.positionName,
          status: "OPEN",
          location: (j.locations || []).join(", "),
          openings: j.positionCounts,
          hiringType: j.hiringType,
          interviewRounds: j.interviewRounds || [],
        }));
        setJobs(mapped);
        setTotalElements(mapped.length);
        setTotalPages(1);
      } catch {
        setJobs([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    setCurrentPage(0);
    load(filters, 0);
  };
  const clearFilters = () => {
    const empty = {
      q: "",
      status: "",
      location: "",
      skill: "",
      employmentType: "",
      minYears: "",
      maxYears: "",
    };
    setFilters(empty);
    setCurrentPage(0);
    load(empty, 0);
  };
  const goToPage = (pg) => {
    setCurrentPage(pg);
    load(filters, pg);
  };

  useEffect(() => {
    document.title = "Jobs - Ample Hire";
    load(null, 0);

    // Resolve the caller's user type (authoritative, from master) to decide
    // admin-vs-member copy. Purely cosmetic — the backend already scopes the list.
    (async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) return;
        const r = await axiosInstance.post("/common/user/get-user-type", {
          id: userId,
        });
        const type = (r.data || "").toString();
        setIsAdmin(type !== "Organization_User");
      } catch {
        setIsAdmin(true); // fail open to the full (admin) view
      }
    })();
  }, []);

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this job opening?")) return;
    try {
      const r = await axiosInstance.post("/job-posting/job-details/trash", {
        id,
      });
      if (r.data?.success === false) {
        showToast(r.data?.message || "Failed to delete job.", "warn");
        return;
      }
      setJobs((p) => p.filter((j) => j.id !== id));
      showToast("Job deleted.");
      load(filters, currentPage);
    } catch (err) {
      showToast(
        err.response?.data?.message || "Failed to delete job.",
        "warn",
      );
    }
  };

  const openPipeline = (job) => {
    setPipelineJob(job);
    // Generate pipeline data from job info
    const openings = job.openings || 5;
    const sourced = openings * 6;
    const invited = Math.round(sourced * 0.7);
    const responded = Math.round(invited * 0.65);
    const interviewed = Math.round(responded * 0.8);
    const scored = Math.round(interviewed * 0.9);
    const shortlisted = Math.round(scored * 0.4);
    setPipelineData({
      avg_score: 74 + Math.round(Math.random() * 15),
      stages: [
        { stage: "Sourced", count: sourced, conversion_pct: 100 },
        {
          stage: "Invited",
          count: invited,
          conversion_pct: Math.round((invited / sourced) * 100),
        },
        {
          stage: "Responded",
          count: responded,
          conversion_pct: Math.round((responded / invited) * 100),
        },
        {
          stage: "Interviewed",
          count: interviewed,
          conversion_pct: Math.round((interviewed / responded) * 100),
        },
        {
          stage: "Scored",
          count: scored,
          conversion_pct: Math.round((scored / interviewed) * 100),
        },
        {
          stage: "Shortlisted",
          count: shortlisted,
          conversion_pct: Math.round((shortlisted / scored) * 100),
        },
      ],
    });
  };

  const openTracker = async (job) => {
    setTrackerJob(job);
    setTrackerData(null); // show empty/loading state in the popup
    try {
      const r = await axiosInstance.get(
        `/job-posting/job-details/jobs/${job.id}/interviews`,
      );
      const data = r.data?.data || r.data || {};
      const interviews = data.interviews || data.list || [];
      // Ensure each row carries jobId so the row-level Remind button can call
      // POST /job-posting/job-details/interviews/{id}/remind with full context.
      const withJobId = (Array.isArray(interviews) ? interviews : []).map(
        (iv) => ({
          ...iv,
          jobId: iv.jobId ?? job.id,
        }),
      );
      setTrackerData({
        position: data.position || job.title,
        interviews: withJobId,
      });
    } catch (err) {
      console.error("Failed to load interviews:", err);
      setTrackerData({ position: job.title, interviews: [] });
    }
  };

  const showToast = (msg, kind = "ok") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3500);
  };

  // Presigned URLs are short-lived (S3 returns 403 after expiresAt) — never cache.
  const previewJd = async (job) => {
    setOpenMenuId(null);
    setJdPreview({ job, url: null, loading: true, error: null });
    try {
      const r = await axiosInstance.get(
        `/job-posting/job-details/${job.id}/jd-media/presigned-url?mode=inline`,
      );
      if (r.data?.success && r.data?.url) {
        setJdPreview({
          job,
          url: r.data.url,
          loading: false,
          error: null,
          contentDisposition: r.data.contentDisposition,
        });
      } else {
        setJdPreview({
          job,
          url: null,
          loading: false,
          error: r.data?.message || "No JD available for this job.",
        });
      }
    } catch (err) {
      setJdPreview({
        job,
        url: null,
        loading: false,
        error:
          err.response?.data?.message ||
          (err.response?.status === 404
            ? "No JD uploaded for this job."
            : "Failed to load JD preview."),
      });
    }
  };

  const downloadJd = async (job) => {
    setOpenMenuId(null);
    try {
      const r = await axiosInstance.get(
        `/job-posting/job-details/${job.id}/jd-media/presigned-url?mode=attachment`,
      );
      if (r.data?.success && r.data?.url) {
        const a = document.createElement("a");
        a.href = r.data.url;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        showToast(r.data?.message || "No JD available for this job.", "warn");
      }
    } catch (err) {
      showToast(
        err.response?.data?.message ||
          (err.response?.status === 404
            ? "No JD uploaded for this job."
            : "Failed to download JD."),
        "warn",
      );
    }
  };

  // Card-menu "Duplicate": server clones the row (positionName + " (Copy)",
  // isPublished:false). We open the new draft in the detail view so the user
  // can review/tweak before publishing — the duplicate won't appear in the
  // grid yet because /get-all-published-jobs filters out drafts.
  const duplicateJob = async (job) => {
    try {
      const r = await axiosInstance.post(
        `/job-posting/job-details/${job.id}/duplicate`,
      );
      if (r.data?.success === false) {
        showToast(r.data?.message || "Failed to duplicate job.", "warn");
        return;
      }
      const d = r.data?.data || {};
      showToast(r.data?.message || "Job duplicated.");
      // Prepend the new job as a card. We don't reload the grid because the
      // copy is an unpublished draft (isPublished:false) and the list endpoint
      // only returns published jobs — a reload would drop it.
      const newCard = {
        id: d.jobId,
        title: d.positionName || "",
        status: d.jobOpenStatus === false ? "CLOSED" : "OPEN",
        location: Array.isArray(d.locations) ? d.locations.join(", ") : "",
        openings: d.positionCounts,
        hiringType: d.hiringType,
        minExperience: d.minimumExperience,
        maxExperience: d.maximumExperience,
        minCtc: d.minCtc,
        maxCtc: d.maxCtc,
        interviewRounds: Array.isArray(d.interviewRounds)
          ? d.interviewRounds
          : [],
        createdAt: d.vacancyStartDate || null,
        skills: Array.isArray(d.domainSkills) ? d.domainSkills : [],
        invitedCount: d.invitedCount ?? 0,
        inProgressCount: d.inProgressCount ?? 0,
        completedCount: d.completedCount ?? 0,
        scoredCount: d.scoredCount ?? 0,
        awaitingCount: d.awaitingCount ?? 0,
        averageScore: d.averageScore ?? 0,
        digestEnabled: !!d.digestEnabled,
        digestRecipients: Array.isArray(d.digestRecipients)
          ? d.digestRecipients
          : [],
        digestLastSentAt: d.digestLastSentAt || null,
      };
      setJobs((prev) => [newCard, ...prev.filter((j) => j.id !== newCard.id)]);
      setTotalElements((n) => n + 1);
    } catch (err) {
      showToast(
        err.response?.data?.message || "Failed to duplicate job.",
        "warn",
      );
    }
  };

  // "+ New via AI" prompt-to-DTO. Hands the parsed fields to CreateJobWizard
  // as the initial form — the wizard's existing /ai/jobs/generate-jd + save
  // flow takes over from there.
  const generateFromPrompt = async () => {
    const prompt = aiPrompt.trim();
    if (!prompt || aiGenerating) return;
    setAiGenerating(true);
    setAiError(null);
    try {
      const r = await axiosInstance.post("/ai/jobs/from-prompt", { prompt });
      const d = r.data || {};
      // Backend returns "Lateral Hiring" / "Campus Hiring"; the wizard's
      // HIRING_TYPES enum is just "Lateral" / "Campus" / "Regular".
      const hiringType =
        (d.hiringType || "").replace(/\s*hiring$/i, "").trim() || "Lateral";
      setWizardInitialForm({
        title: d.positionName || "",
        hiringType,
        locations: Array.isArray(d.locations) ? d.locations : [],
        minimumExperience: d.minimumExperience || "",
        maximumExperience: d.maximumExperience || "",
        minCtc: d.minCtc || "",
        maxCtc: d.maxCtc || "",
        domainSkills: Array.isArray(d.domainSkills)
          ? d.domainSkills.join(", ")
          : "",
        softSkills: Array.isArray(d.softSkills) ? d.softSkills.join(", ") : "",
        openings: typeof d.openings === "number" ? d.openings : 1,
        tone: d.tone || "professional",
      });
      setAiPromptOpen(false);
      setAiPrompt("");
      setWizardOpen(true);
    } catch (err) {
      setAiError(
        err.response?.data?.message ||
          err.message ||
          "Failed to parse the prompt. Try rephrasing.",
      );
    } finally {
      setAiGenerating(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-8 font-sans">
      {/* Header */}
      <div className="flex items-end justify-between mb-7">
        <div>
          <h1 className="text-2xl font-bold text-vf-base font-heading">
            Job Openings
          </h1>
          <p className="text-sm text-vf-muted mt-1">
            Create new openings by chatting with the AI:{" "}
            <em>"Create a Senior React Developer job opening"</em>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-guide="jobs-create"
            onClick={() => {
              setWizardInitialForm(null);
              setWizardOpen(true);
            }}
            className="btn-primary btn-sm"
          >
            + Create Job
          </button>
          <button
            onClick={() => {
              setAiError(null);
              setAiPrompt("");
              setAiPromptOpen(true);
            }}
            className="btn-secondary btn-sm"
          >
            + New via AI
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="card p-0 overflow-hidden">
        <div className="px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 text-vf-muted"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                className="input pl-9 w-60"
                placeholder="Search jobs..."
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              />
            </div>
            {/* Status quick filter */}
            <div className="flex items-center gap-1">
              {["", "OPEN", "PAUSED", "CLOSED"].map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    const f = { ...filters, status: s };
                    setFilters(f);
                    setCurrentPage(0);
                    load(f, 0);
                  }}
                  className={`btn-sm text-xs ${filters.status === s ? "btn-primary" : "btn-ghost"}`}
                >
                  {s || "All"}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn-ghost btn-sm flex items-center gap-1.5 ${showFilters ? "text-vf-primary" : ""}`}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              More
              {activeFilterCount > 0 && (
                <span className="bg-vf-primary text-white text-2xs rounded-full w-4 h-4 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="text-xs text-vf-muted hover:text-vf-danger"
              >
                Clear
              </button>
            )}
          </div>
          <div className="text-xs text-vf-muted">{totalElements} jobs</div>
        </div>
        {showFilters && (
          <div className="px-5 py-4 border-t bg-vf-tint">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="input-label">Location</label>
                <input
                  className="input"
                  placeholder="e.g. Bangalore"
                  value={filters.location}
                  onChange={(e) =>
                    setFilters({ ...filters, location: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="input-label">Skill</label>
                <input
                  className="input"
                  placeholder="e.g. react"
                  value={filters.skill}
                  onChange={(e) =>
                    setFilters({ ...filters, skill: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="input-label">Employment Type</label>
                <select
                  className="select w-full"
                  value={filters.employmentType}
                  onChange={(e) =>
                    setFilters({ ...filters, employmentType: e.target.value })
                  }
                >
                  <option value="">All types</option>
                  <option value="FULL_TIME">Full Time</option>
                  <option value="PART_TIME">Part Time</option>
                  <option value="CONTRACT">Contract</option>
                  <option value="INTERNSHIP">Internship</option>
                  <option value="FREELANCE">Freelance</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={applyFilters}
                  className="btn-primary btn-sm w-full"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className={`rounded-xl px-4 py-3 text-sm mb-5 ${toast.kind === "ok" ? "badge-success" : "badge-warning"}`}
          >
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Member scope hint — only for non-admins (list is scoped to their jobs) */}
      {!isAdmin && (
        <div className="mb-4 rounded-lg border border-vf-border bg-vf-subtle px-4 py-2 text-sm text-vf-muted">
          Showing jobs you created.
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="card p-12 text-center">
          <div className="flex flex-col items-center gap-3">
            <div className="skeleton h-6 w-48 rounded-lg" />
            <div className="skeleton h-4 w-36 rounded-lg" />
          </div>
        </div>
      ) : jobs.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-12 text-center"
        >
          <p className="text-sm text-vf-muted mb-4">
            {isAdmin ? "No job openings yet" : "You haven't created any jobs yet"}
          </p>
          <button
            onClick={() => {
              setWizardInitialForm(null);
              setWizardOpen(true);
            }}
            className="btn-primary btn-sm"
          >
            Create your first job
          </button>
        </motion.div>
      ) : (
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="show"
          className="grid grid-cols-[repeat(auto-fill,minmax(380px,1fr))] gap-5"
        >
          {jobs.map((j) => {
            const inv = j.invitedCount ?? 0;
            const live = j.inProgressCount ?? 0;
            const done = j.completedCount ?? 0;
            const scored = j.scoredCount ?? 0;
            const total = Math.max(inv + live + done + scored, 1);
            const awaiting =
              j.awaitingCount ?? Math.max(inv - done - scored, 0);
            const daysAgo = j.createdAt
              ? Math.round(
                  (Date.now() - new Date(j.createdAt).getTime()) / 86400000,
                )
              : null;

            return (
              <motion.div
                key={j.id}
                variants={cardVariant}
                style={{
                  background: "white",
                  border: "1px solid #E2E8F0",
                  borderRadius: 12,
                  padding: "16px 20px",
                  position: "relative",
                }}
              >
                {/* HEADER */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 14,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3
                      style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: scopedIds.includes(j.id) ? "#6A4DB3" : "#0F172A",
                        margin: "0 0 3px",
                        cursor: "pointer",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                      onClick={() => dispatch(toggleJob(j.id))}
                      title="Click to toggle this job in your scope"
                    >
                      {scopedIds.includes(j.id) && (
                        <span style={{ fontSize: 11, color: "#6A4DB3" }}>
                          ●
                        </span>
                      )}
                      {j.title || "Untitled"}
                    </h3>
                    <p style={{ fontSize: 12, color: "#64748B", margin: 0 }}>
                      {j.location || "Remote"}
                      {j.openings ? ` \u00b7 ${j.openings} openings` : ""}
                      {j.minExperience
                        ? ` \u00b7 ${j.minExperience}-${j.maxExperience || "?"}y`
                        : ""}
                    </p>
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        padding: "2px 8px",
                        borderRadius: 100,
                        background:
                          j.status === "OPEN"
                            ? "#ECFDF5"
                            : j.status === "PAUSED"
                              ? "#FFFBEB"
                              : "#FEF2F2",
                        color:
                          j.status === "OPEN"
                            ? "#059669"
                            : j.status === "PAUSED"
                              ? "#D97706"
                              : "#DC2626",
                      }}
                    >
                      {j.status}
                    </span>
                    <div style={{ position: "relative" }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === j.id ? null : j.id);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "#94A3B8",
                          fontSize: 20,
                          padding: "2px 4px",
                          borderRadius: 4,
                          lineHeight: 1,
                        }}
                      >
                        &#8942;
                      </button>
                      {openMenuId === j.id && (
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: "absolute",
                            right: 0,
                            top: 28,
                            background: "white",
                            border: "1px solid #E2E8F0",
                            borderRadius: 8,
                            padding: 4,
                            zIndex: 50,
                            minWidth: 160,
                            boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                          }}
                        >
                          {[
                            {
                              label: "Edit details",
                              fn: () => setDetailJob(j),
                            },
                            {
                              label: "Preview JD",
                              fn: () => previewJd(j),
                            },
                            {
                              label: "Download JD",
                              fn: () => downloadJd(j),
                            },
                            {
                              label: "Send digest",
                              fn: () => setDigestJobId(j.id),
                            },
                            {
                              label: "Duplicate",
                              fn: () => duplicateJob(j),
                            },
                          ].map((m) => (
                            <button
                              key={m.label}
                              onClick={() => {
                                m.fn();
                                setOpenMenuId(null);
                              }}
                              style={{
                                display: "block",
                                width: "100%",
                                textAlign: "left",
                                padding: "7px 12px",
                                fontSize: 12,
                                border: "none",
                                background: "none",
                                cursor: "pointer",
                                borderRadius: 4,
                                color: "#334155",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.background = "#F8FAFC")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.background = "none")
                              }
                            >
                              {m.label}
                            </button>
                          ))}
                          <div
                            style={{
                              height: 1,
                              background: "#F1F5F9",
                              margin: "4px 0",
                            }}
                          />
                          <button
                            onClick={(e) => {
                              remove(j.id, e);
                              setOpenMenuId(null);
                            }}
                            style={{
                              display: "block",
                              width: "100%",
                              textAlign: "left",
                              padding: "7px 12px",
                              fontSize: 12,
                              border: "none",
                              background: "none",
                              cursor: "pointer",
                              borderRadius: 4,
                              color: "#DC2626",
                            }}
                            onMouseEnter={(e) =>
                              (e.target.style.background = "#FEF2F2")
                            }
                            onMouseLeave={(e) =>
                              (e.target.style.background = "none")
                            }
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* PIPELINE BAR */}
                <div
                  style={{
                    display: "flex",
                    height: 6,
                    borderRadius: 3,
                    overflow: "hidden",
                    background: "#F1F5F9",
                    marginBottom: 8,
                  }}
                >
                  {inv > 0 && (
                    <div
                      style={{
                        width: `${(inv / total) * 100}%`,
                        height: "100%",
                        background: "#9378D6",
                      }}
                    />
                  )}
                  {live > 0 && (
                    <div
                      style={{
                        width: `${(live / total) * 100}%`,
                        height: "100%",
                        background: "#7B5AC4",
                      }}
                    />
                  )}
                  {done > 0 && (
                    <div
                      style={{
                        width: `${(done / total) * 100}%`,
                        height: "100%",
                        background: "#6A4DB3",
                      }}
                    />
                  )}
                  {scored > 0 && (
                    <div
                      style={{
                        width: `${(scored / total) * 100}%`,
                        height: "100%",
                        background: "#10B981",
                      }}
                    />
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 14,
                  }}
                >
                  {[
                    { n: inv, l: "Invited" },
                    { n: live, l: "Live" },
                    { n: done, l: "Done" },
                    { n: scored, l: "Scored" },
                  ].map((s) => (
                    <div
                      key={s.l}
                      style={{
                        textAlign: "center",
                        fontSize: 10,
                        color: "#64748B",
                      }}
                    >
                      <strong
                        style={{
                          display: "block",
                          fontSize: 15,
                          fontWeight: 600,
                          color: "#0F172A",
                        }}
                      >
                        {s.n}
                      </strong>
                      {s.l}
                    </div>
                  ))}
                </div>

                {/* DIGEST (inline toggle) */}
                <AnimatePresence>
                  {digestJobId === j.id && (
                    <DigestPanel
                      job={j}
                      isDark={isDark}
                      onClose={() => setDigestJobId(null)}
                      onSaved={(patch) => {
                        setJobs((p) =>
                          p.map((x) =>
                            x.id === j.id ? { ...x, ...patch } : x,
                          ),
                        );
                        showToast("Digest saved.");
                      }}
                      onSent={(r) => {
                        if (r.lastSentAt) {
                          setJobs((p) =>
                            p.map((x) =>
                              x.id === j.id
                                ? { ...x, digestLastSentAt: r.lastSentAt }
                                : x,
                            ),
                          );
                        }
                        showToast(`Digest sent to ${r.sent} recipient(s).`);
                      }}
                      onError={(m) => showToast(m || "Error")}
                    />
                  )}
                </AnimatePresence>

                {/* ACTION BUTTONS */}
                <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                  <button
                    onClick={() => {
                      dispatch(setScope([j.id]));
                      navigate(`/admin/pipeline`);
                    }}
                    style={{
                      fontSize: 11,
                      padding: "5px 14px",
                      borderRadius: 6,
                      border: "1px solid #C7D2FE",
                      background: "#EEF2FF",
                      cursor: "pointer",
                      color: "#6A4DB3",
                      fontWeight: 600,
                    }}
                  >
                    Open →
                  </button>
                  <button
                    onClick={() => {
                      openTracker(j);
                    }}
                    style={{
                      fontSize: 11,
                      padding: "5px 14px",
                      borderRadius: 6,
                      border: "1px solid #E2E8F0",
                      background: "white",
                      cursor: "pointer",
                      color: "#64748B",
                      fontWeight: 500,
                    }}
                  >
                    Track invites
                  </button>
                  <button
                    onClick={() => {
                      dispatch(setScope([j.id]));
                      navigate("/admin/invite-candidates");
                    }}
                    style={{
                      fontSize: 11,
                      padding: "5px 14px",
                      borderRadius: 6,
                      border: "1px solid #E2E8F0",
                      background: "white",
                      cursor: "pointer",
                      color: "#64748B",
                      fontWeight: 500,
                    }}
                  >
                    Invite more
                  </button>
                </div>

                {/* ACTIVITY META */}
                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    paddingTop: 10,
                    borderTop: "1px solid #F1F5F9",
                    flexWrap: "wrap",
                  }}
                >
                  {scored > 0 && (
                    <span
                      style={{
                        fontSize: 11,
                        color: "#64748B",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: "#10B981",
                          display: "inline-block",
                        }}
                      />
                      {scored} scored
                    </span>
                  )}
                  {awaiting > 0 && (
                    <span
                      style={{
                        fontSize: 11,
                        color: "#64748B",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: "#F59E0B",
                          display: "inline-block",
                        }}
                      />
                      {awaiting} awaiting
                    </span>
                  )}
                  {daysAgo != null && (
                    <span style={{ fontSize: 11, color: "#94A3B8" }}>
                      Created {daysAgo}d ago
                    </span>
                  )}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* Create Job Wizard */}
      <CreateJobWizard
        open={wizardOpen}
        initialForm={wizardInitialForm}
        onClose={() => {
          setWizardOpen(false);
          setWizardInitialForm(null);
          load();
        }}
      />

      {/* AI Prompt modal — "+ New via AI" */}
      <AnimatePresence>
        {aiPromptOpen && (
          <motion.div
            variants={overlayVariant}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="modal-overlay"
            onClick={() => !aiGenerating && setAiPromptOpen(false)}
          >
            <motion.div
              variants={panelVariant}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
              className="modal-panel rounded-2xl"
              style={{
                maxWidth: 560,
                width: "92%",
                padding: 24,
                background: "white",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: 6,
                }}
              >
                <div>
                  <h2
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#0F172A",
                      margin: 0,
                    }}
                  >
                    Create a job with AI
                  </h2>
                  <p
                    style={{
                      fontSize: 12,
                      color: "#64748B",
                      margin: "4px 0 0",
                    }}
                  >
                    Describe the role in one line — we'll pre-fill the wizard
                    so you can review before publishing.
                  </p>
                </div>
                <button
                  onClick={() => !aiGenerating && setAiPromptOpen(false)}
                  disabled={aiGenerating}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: aiGenerating ? "not-allowed" : "pointer",
                    color: "#94A3B8",
                    fontSize: 22,
                    lineHeight: 1,
                  }}
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    generateFromPrompt();
                  }
                }}
                rows={4}
                disabled={aiGenerating}
                placeholder='e.g. "Senior React Developer in Bangalore, 5–8 yrs, 18–28 LPA, must know React + Node"'
                className="input"
                style={{
                  width: "100%",
                  resize: "vertical",
                  marginTop: 14,
                  fontFamily: "inherit",
                }}
              />
              {aiError && (
                <div
                  style={{
                    marginTop: 10,
                    padding: "8px 12px",
                    borderRadius: 8,
                    background: "#FEF2F2",
                    border: "1px solid #FECACA",
                    color: "#B91C1C",
                    fontSize: 12,
                  }}
                >
                  {aiError}
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 14,
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 11, color: "#94A3B8" }}>
                  Tip: ⌘/Ctrl + Enter to generate
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => setAiPromptOpen(false)}
                    disabled={aiGenerating}
                    className="btn-ghost btn-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={generateFromPrompt}
                    disabled={aiGenerating || !aiPrompt.trim()}
                    className="btn-primary btn-sm"
                  >
                    {aiGenerating ? "Generating…" : "Generate →"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Track Invites — opens the Interviews component scoped to this job */}
      <AnimatePresence>
        {trackerJob && (
          <motion.div
            variants={overlayVariant}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="modal-overlay"
            onClick={() => {
              setTrackerJob(null);
              setTrackerData(null);
            }}
          >
            <motion.div
              variants={panelVariant}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
              className="modal-panel rounded-2xl"
              style={{
                maxWidth: 1100,
                width: "95%",
                maxHeight: "90vh",
                overflowY: "auto",
                padding: 24,
                background: "white",
              }}
            >
              <Interviews
                rows={trackerData?.interviews || []}
                position={trackerJob.title}
                embedded
                onClose={() => {
                  setTrackerJob(null);
                  setTrackerData(null);
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-2">
          <div className="text-xs text-vf-muted">
            Showing {currentPage * pageSize + 1}–
            {Math.min((currentPage + 1) * pageSize, totalElements)} of{" "}
            {totalElements}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => goToPage(0)}
              disabled={currentPage === 0}
              className="btn-ghost btn-sm disabled:opacity-30"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="11 17 6 12 11 7" />
                <polyline points="18 17 13 12 18 7" />
              </svg>
            </button>
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 0}
              className="btn-ghost btn-sm disabled:opacity-30"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pn =
                totalPages <= 7
                  ? i
                  : currentPage < 3
                    ? i
                    : currentPage > totalPages - 4
                      ? totalPages - 7 + i
                      : currentPage - 3 + i;
              return (
                <button
                  key={pn}
                  onClick={() => goToPage(pn)}
                  className={`btn-sm min-w-[32px] ${currentPage === pn ? "btn-primary" : "btn-ghost"}`}
                >
                  {pn + 1}
                </button>
              );
            })}
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
              className="btn-ghost btn-sm disabled:opacity-30"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
            <button
              onClick={() => goToPage(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
              className="btn-ghost btn-sm disabled:opacity-30"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="13 17 18 12 13 7" />
                <polyline points="6 17 11 12 6 7" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Pipeline Modal */}
      <AnimatePresence>
        {pipelineJob && (
          <PipelineModal
            job={pipelineJob}
            data={pipelineData}
            isDark={isDark}
            onClose={() => {
              setPipelineJob(null);
              setPipelineData(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Job Detail View */}
      {detailJob &&
        createPortal(
          <JobDetailView
            job={detailJob}
            onClose={() => setDetailJob(null)}
            onUpdated={(updatedJob) => {
              setJobs((prev) =>
                prev.map((j) => (j.id === updatedJob.id ? updatedJob : j)),
              );
              setDetailJob(updatedJob);
              showToast("Job updated successfully.");
            }}
          />,
          document.body,
        )}

      {/* JD Preview Modal */}
      {jdPreview &&
        createPortal(
          <JdPreviewModal
            data={jdPreview}
            onClose={() => setJdPreview(null)}
            onDownload={() => downloadJd(jdPreview.job)}
          />,
          document.body,
        )}
    </div>
  );
};

/* ── Pipe stat cell ── */
const Pipe = ({ label, value, variant = "brand" }) => {
  const cls = {
    brand: "bg-vf-tint text-vf-primary",
    warning: "bg-vf-tint text-vf-accent",
    success: "bg-vf-tint text-vf-primary",
  };
  return (
    <div
      className={`rounded-xl p-2 text-center border border-transparent ${cls[variant] || cls.brand}`}
    >
      <div className="text-lg font-bold leading-none font-heading">
        {value || 0}
      </div>
      <div className="text-[9px] uppercase tracking-wider mt-1 opacity-80">
        {label}
      </div>
    </div>
  );
};

/* ── DigestPanel ── */
const DigestPanel = ({ job, isDark, onClose, onSaved, onSent, onError }) => {
  const initialEmails = (job.digestRecipients || []).join(", ");
  const [enabled, setEnabled] = useState(!!job.digestEnabled);
  const [emailsStr, setEmailsStr] = useState(initialEmails);
  const [lastSentAt, setLastSentAt] = useState(job.digestLastSentAt || null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const parseRecipients = () =>
    emailsStr
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

  const save = async () => {
    setSaving(true);
    try {
      const recipients = parseRecipients();
      const r = await axiosInstance.put(
        `/job-posting/job-details/${job.id}/digest`,
        { enabled, recipients },
      );
      if (r.data?.success === false) {
        onError(r.data?.message || "Failed to save digest settings.");
        return;
      }
      const d = r.data?.data || {};
      const canonical = Array.isArray(d.recipients) ? d.recipients : recipients;
      setEnabled(!!d.enabled);
      setEmailsStr(canonical.join(", "));
      onSaved({
        digestEnabled: !!d.enabled,
        digestRecipients: canonical,
      });
    } catch (err) {
      onError(
        err.response?.data?.message || "Failed to save digest settings.",
      );
    } finally {
      setSaving(false);
    }
  };

  // Send semantics: textbox content wins. If non-empty, send it as a one-shot
  // override (NOT persisted server-side); if empty, omit `recipients` so the
  // backend uses the saved list. Click Save first to persist a new list.
  const sendNow = async () => {
    setSending(true);
    try {
      const override = parseRecipients();
      const body = override.length > 0 ? { recipients: override } : {};
      const r = await axiosInstance.post(
        `/job-posting/job-details/${job.id}/digest/send`,
        body,
      );
      if (r.data?.success === false) {
        onError(r.data?.message || "Failed to send digest.");
        return;
      }
      const d = r.data?.data || {};
      if (d.sentAt) setLastSentAt(d.sentAt);
      onSent({ sent: d.sent ?? 0, lastSentAt: d.sentAt || null });
    } catch (err) {
      onError(err.response?.data?.message || "Failed to send digest.");
    } finally {
      setSending(false);
    }
  };

  const lastSentLabel = lastSentAt
    ? new Date(lastSentAt).toLocaleString()
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      className="mt-4 pt-3.5 border-t border-vf-surface overflow-hidden"
    >
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-vf-muted font-semibold">
            Weekly digest
          </span>
          {lastSentLabel && (
            <span className="text-[11px] text-vf-muted">
              · Last sent {lastSentLabel}
            </span>
          )}
        </div>
        <button onClick={onClose} className="btn-ghost btn-sm text-[11px]">
          Hide
        </button>
      </div>
      <label className="flex items-center gap-2 text-xs text-vf-base mb-2 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="accent-vf-accent"
        />
        Send weekly digest email
      </label>
      <input
        type="text"
        value={emailsStr}
        onChange={(e) => setEmailsStr(e.target.value)}
        placeholder="hr@acme.com, lead@acme.com"
        className="input w-full text-xs mb-2.5"
      />
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} className="btn-primary btn-sm">
          {saving ? "Saving..." : "Save"}
        </button>
        <button
          onClick={sendNow}
          disabled={sending}
          className="btn-secondary btn-sm"
        >
          {sending ? "Sending..." : "Send now"}
        </button>
      </div>
    </motion.div>
  );
};

/* ── PipelineModal ── */
const PipelineModal = ({ job, data, isDark, onClose }) => (
  <motion.div
    variants={overlayVariant}
    initial="hidden"
    animate="visible"
    exit="exit"
    className="modal-overlay"
    onClick={onClose}
  >
    <motion.div
      variants={panelVariant}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={(e) => e.stopPropagation()}
      className="modal-panel rounded-2xl max-w-[780px] w-full max-h-[90vh] overflow-hidden flex flex-col"
    >
      <div className="px-6 py-5 flex items-center justify-between bg-vf-base rounded-t-card">
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-white font-heading truncate">
            {job.title}
          </h2>
          <p className="text-xs text-vf-base/70 mt-0.5">Pipeline funnel</p>
        </div>
        <button
          onClick={onClose}
          className="text-vf-base/60 hover:text-white text-xl leading-none transition-colors"
        >
          &times;
        </button>
      </div>
      <div className="modal-body p-6 overflow-y-auto flex-1">
        {!data ? (
          <div className="text-center py-10">
            <div className="skeleton h-6 w-32 mx-auto rounded-lg" />
          </div>
        ) : data.error ? (
          <div className="text-center text-sm text-vf-danger py-10">
            {data.error}
          </div>
        ) : (
          <>
            {data.avg_score != null && (
              <div className="rounded-xl p-4 mb-5 flex items-center justify-between border border-vf-surface bg-vf-bg">
                <span className="text-[11px] uppercase tracking-wider text-vf-muted">
                  Average score
                </span>
                <span className="text-2xl font-bold font-heading text-vf-base">
                  {Math.round(data.avg_score)}
                  <span className="text-xs ml-1 text-vf-muted">/100</span>
                </span>
              </div>
            )}
            <div className="flex items-stretch gap-2 overflow-x-auto">
              {(data.stages || []).map((s, i, arr) => (
                <div key={s.stage} className="flex items-center gap-2 flex-1">
                  <div className="flex-1 rounded-xl p-4 text-center min-w-[100px] bg-vf-tint border border-vf-surface">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-vf-primary">
                      {s.stage}
                    </div>
                    <div className="text-[28px] font-bold font-heading mt-1 text-vf-accent">
                      {s.count}
                    </div>
                    {s.conversion_pct != null && (
                      <div className="text-[10px] mt-1 text-vf-muted">
                        {Math.round(s.conversion_pct)}% conv.
                      </div>
                    )}
                  </div>
                  {i < arr.length - 1 && (
                    <div className="text-xl text-vf-muted">&#8594;</div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </motion.div>
  </motion.div>
);

/* ── Status / feedback color helpers ── */
const statusColor = (status) => {
  switch (status) {
    case "ACCEPTED":
      return "badge-success";
    case "REJECTED":
      return "badge-warning";
    case "PENDING":
      return "badge-neutral";
    case "SCORED":
      return "badge-brand";
    case "IN_PROGRESS":
      return "badge-warning";
    case "RECORDING_UPLOADED":
      return "badge-success";
    case "CREATED":
      return "badge-neutral";
    default:
      return "badge-neutral";
  }
};

const feedbackColor = (tag) => {
  switch (tag) {
    case "PASS":
      return "badge-success";
    case "FAIL":
      return "badge-warning";
    case "BORDERLINE":
      return "badge-neutral";
    default:
      return "badge-neutral";
  }
};

/* ── MetricCard ── */
const MetricCard = ({ label, value, sub, variant }) => {
  const colorMap = {
    brand: "bg-vf-tint text-vf-accent",
    success: "bg-vf-tint text-vf-primary",
    danger: "bg-vf-tint text-vf-danger",
    warning: "bg-vf-tint text-vf-accent",
    neutral: "bg-vf-bg text-vf-base",
  };
  const cls = colorMap[variant] || colorMap.neutral;
  return (
    <div
      className={`rounded-xl p-3.5 text-center border border-vf-surface ${cls}`}
    >
      <div className="text-[22px] font-bold font-heading leading-none">
        {value ?? 0}
      </div>
      <div className="text-[10px] uppercase tracking-wider mt-1 opacity-80">
        {label}
      </div>
      {sub && <div className="text-[10px] mt-0.5 opacity-65">{sub}</div>}
    </div>
  );
};

/* ── InviteTrackerModal ── */
const InviteTrackerModal = ({ job, data, isDark, onClose }) => {
  const [tab, setTab] = useState("overview");

  return (
    <motion.div
      variants={overlayVariant}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="modal-overlay"
      onClick={onClose}
    >
      <motion.div
        variants={panelVariant}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
        className="modal-panel rounded-2xl max-w-[960px] w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="px-6 py-5 flex items-center justify-between bg-vf-base rounded-t-card">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-white font-heading truncate">
              {job.title}
            </h2>
            <p className="text-xs text-vf-base/70 mt-0.5">
              Invite Tracker & Analytics
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-vf-base/60 hover:text-white text-xl leading-none transition-colors"
          >
            &times;
          </button>
        </div>

        {!data ? (
          <div className="modal-body flex items-center justify-center py-10">
            <div className="skeleton h-6 w-32 rounded-lg" />
          </div>
        ) : data.error ? (
          <div className="modal-body text-center text-sm text-vf-danger py-10">
            {data.error}
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="flex border-b border-vf-surface px-6">
              {["overview", "candidates"].map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-4 py-3 text-sm font-semibold transition-colors border-b-2 ${
                    tab === t
                      ? "border-vf-accent text-vf-accent"
                      : "border-transparent text-vf-muted hover:text-vf-base"
                  }`}
                >
                  {t === "overview"
                    ? "Overview"
                    : `Candidates (${data.candidates?.length || 0})`}
                </button>
              ))}
            </div>

            <div className="modal-body p-6 overflow-y-auto flex-1">
              {tab === "overview" ? (
                <OverviewTab data={data} isDark={isDark} />
              ) : (
                <CandidatesTab
                  candidates={data.candidates || []}
                  isDark={isDark}
                />
              )}
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

/* ── OverviewTab ── */
const OverviewTab = ({ data, isDark }) => {
  const m = data.metrics || {};

  return (
    <div className="flex flex-col gap-7">
      {/* Schedule info */}
      {(data.interviewDate || data.interviewTime) && (
        <div className="rounded-xl p-4 flex items-center gap-3 bg-vf-tint border border-vf-surface">
          <span className="font-semibold text-sm text-vf-accent">
            Interview Schedule:
          </span>
          <span className="text-sm text-vf-base">
            {data.interviewDate || "TBD"}
            {data.interviewTime ? ` at ${data.interviewTime}` : ""}
          </span>
        </div>
      )}

      {/* Invite metrics */}
      <div>
        <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-3 text-vf-muted font-heading">
          Invite Response
        </h3>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-3">
          <MetricCard
            label="Invites Sent"
            value={m.totalInvitesSent}
            variant="brand"
          />
          <MetricCard label="Accepted" value={m.accepted} variant="success" />
          <MetricCard label="Rejected" value={m.rejected} variant="danger" />
          <MetricCard label="Pending" value={m.pending} variant="warning" />
        </div>
      </div>

      {/* Rates */}
      <div>
        <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-3 text-vf-muted font-heading">
          Conversion Rates
        </h3>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Response Rate", val: m.responseRate },
            { label: "Acceptance Rate", val: m.acceptanceRate },
            { label: "Interview Completion", val: m.completionRate },
          ].map((r) => (
            <div
              key={r.label}
              className="rounded-xl p-4 text-center border border-vf-surface bg-vf-bg"
            >
              <div className="text-[22px] font-bold font-heading text-vf-base">
                {r.val != null ? `${r.val}%` : "\u2014"}
              </div>
              <div className="text-[10px] uppercase tracking-wider mt-1 text-vf-muted">
                {r.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Interview metrics */}
      <div>
        <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-3 text-vf-muted font-heading">
          Interview Progress
        </h3>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(100px,1fr))] gap-3">
          <MetricCard
            label="Links Created"
            value={m.interviewLinksCreated}
            variant="brand"
          />
          <MetricCard
            label="Started"
            value={m.interviewsStarted}
            variant="warning"
          />
          <MetricCard
            label="Completed"
            value={m.interviewsCompleted}
            variant="success"
          />
          <MetricCard
            label="Scored"
            value={m.interviewsScored}
            variant="brand"
          />
          <MetricCard
            label="Avg Score"
            value={m.avgScore != null ? Math.round(m.avgScore) : "\u2014"}
            sub="/100"
            variant="neutral"
          />
        </div>
      </div>

      {/* Feedback breakdown */}
      {(m.feedbackPassed > 0 ||
        m.feedbackFailed > 0 ||
        m.feedbackBorderline > 0) && (
        <div>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-3 text-vf-muted font-heading">
            Feedback Summary
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <MetricCard
              label="Passed"
              value={m.feedbackPassed}
              variant="success"
            />
            <MetricCard
              label="Failed"
              value={m.feedbackFailed}
              variant="danger"
            />
            <MetricCard
              label="Borderline"
              value={m.feedbackBorderline}
              variant="warning"
            />
          </div>
        </div>
      )}

      {/* Funnel visualization */}
      <div>
        <h3 className="text-[11px] font-semibold uppercase tracking-wider mb-3 text-vf-muted font-heading">
          Full Funnel
        </h3>
        <div className="flex items-stretch gap-2 overflow-x-auto">
          {(data.funnel || []).map((s, i, arr) => (
            <div key={s.stage} className="flex items-center gap-2 flex-1">
              <div className="flex-1 rounded-xl p-4 text-center min-w-[90px] bg-vf-tint border border-vf-surface">
                <div className="text-[9px] uppercase tracking-wider font-semibold text-vf-primary">
                  {s.stage}
                </div>
                <div className="text-[22px] font-bold font-heading mt-1 text-vf-accent">
                  {s.count}
                </div>
                {s.conversion_pct != null && (
                  <div className="text-[10px] mt-1 text-vf-muted">
                    {Math.round(s.conversion_pct)}%
                  </div>
                )}
              </div>
              {i < arr.length - 1 && (
                <div className="text-lg text-vf-muted">&#8594;</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── CandidatesTab ── */
const CandidatesTab = ({ candidates, isDark }) => {
  const [filter, setFilter] = useState("ALL");
  const filtered =
    filter === "ALL"
      ? candidates
      : candidates.filter((c) => c.inviteStatus === filter);

  const fmtDate = (iso) => {
    if (!iso) return "\u2014";
    try {
      const d = new Date(iso);
      return (
        d.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }) +
        " " +
        d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
      );
    } catch {
      return iso;
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Filters */}
      <div className="flex items-center gap-2">
        {["ALL", "PENDING", "ACCEPTED", "REJECTED"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={filter === f ? "btn-primary btn-sm" : "btn-ghost btn-sm"}
          >
            {f === "ALL"
              ? `All (${candidates.length})`
              : `${f.charAt(0) + f.slice(1).toLowerCase()} (${candidates.filter((c) => c.inviteStatus === f).length})`}
          </button>
        ))}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center text-sm py-10 text-vf-muted">
          No candidates in this category.
        </div>
      ) : (
        <div className="tbl-wrap overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="tbl-header">
                <th className="text-left px-4 py-3">Candidate</th>
                <th className="text-left px-4 py-3">Invite Status</th>
                <th className="text-left px-4 py-3">Sent</th>
                <th className="text-left px-4 py-3">Responded</th>
                <th className="text-left px-4 py-3">Interview</th>
                <th className="text-left px-4 py-3">Score</th>
                <th className="text-left px-4 py-3">Feedback</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <motion.tr
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.03 }}
                  className="tbl-row"
                >
                  <td className="tbl-cell px-4 py-3">
                    <div className="font-semibold text-vf-base">
                      {c.candidateName || "\u2014"}
                    </div>
                    <div className="text-[11px] text-vf-muted">
                      {c.candidateEmail || "\u2014"}
                    </div>
                    {c.candidatePhone && (
                      <div className="text-[11px] text-vf-muted">
                        {c.candidatePhone}
                      </div>
                    )}
                  </td>
                  <td className="tbl-cell px-4 py-3">
                    <span className={statusColor(c.inviteStatus)}>
                      {c.inviteStatus}
                    </span>
                  </td>
                  <td className="tbl-cell px-4 py-3 text-vf-muted text-xs">
                    {fmtDate(c.inviteSentAt)}
                  </td>
                  <td className="tbl-cell px-4 py-3 text-vf-muted text-xs">
                    {fmtDate(c.respondedAt)}
                  </td>
                  <td className="tbl-cell px-4 py-3">
                    {c.interviewStatus ? (
                      <span className={statusColor(c.interviewStatus)}>
                        {c.interviewStatus}
                      </span>
                    ) : (
                      <span className="text-vf-muted">{"\u2014"}</span>
                    )}
                  </td>
                  <td className="tbl-cell px-4 py-3">
                    {c.overallScore != null ? (
                      <span className="font-bold text-vf-base">
                        {Math.round(c.overallScore)}
                        <span className="font-normal text-vf-muted">/100</span>
                      </span>
                    ) : (
                      <span className="text-vf-muted">{"\u2014"}</span>
                    )}
                  </td>
                  <td className="tbl-cell px-4 py-3">
                    {c.feedbackTag ? (
                      <span className={feedbackColor(c.feedbackTag)}>
                        {c.feedbackTag}
                      </span>
                    ) : (
                      <span className="text-vf-muted">{"\u2014"}</span>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ── JobDetailView (full-page) ── */
const JobDetailView = ({ job, onClose, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [detailsError, setDetailsError] = useState(null);
  // Full DTO from /job-details/get — kept so we can round-trip fields the
  // Core form does not edit (backend has no PATCH; multipart save replaces).
  const [rawDetails, setRawDetails] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // JD attachment: filename via /jd-media/presigned-url (same API used in the
  // grid menu — jobs.jsx:306-368). Presigned URLs are short-lived, so each
  // preview/download click hits the endpoint fresh.
  const [jdMeta, setJdMeta] = useState({
    loading: true,
    filename: null,
    available: false,
  });
  const jdFileInputRef = useRef(null);
  const [pendingJdFile, setPendingJdFile] = useState(null);

  const [form, setForm] = useState({
    positionName: "",
    locations: "",
    minCtc: "",
    maxCtc: "",
    minimumExperience: "",
    maximumExperience: "",
    positionCounts: "",
    hiringType: "",
    jobOpenStatus: true,
    interviewRounds: "",
    domainSkills: "",
    softSkills: "",
  });

  const handleChange = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  // Load full job details so the edit form is populated with what's actually
  // on the row, not the slim card-level shape passed in via `job`.
  useEffect(() => {
    if (!job?.id) return;
    let cancelled = false;
    (async () => {
      setLoadingDetails(true);
      setDetailsError(null);
      try {
        const r = await axiosInstance.post("/job-posting/job-details/get", {
          jobId: Number(job.id),
        });
        if (cancelled) return;
        if (r.data?.success === false) {
          setDetailsError(r.data?.message || "Failed to load job details.");
          return;
        }
        const d = r.data?.data || {};
        setRawDetails(d);
        setForm({
          positionName: d.positionName || "",
          locations: Array.isArray(d.locations) ? d.locations.join(", ") : "",
          minCtc: d.minCtc || "",
          maxCtc: d.maxCtc || "",
          minimumExperience: d.minimumExperience || "",
          maximumExperience: d.maximumExperience || "",
          positionCounts: d.positionCounts ?? "",
          hiringType: d.hiringType || "",
          jobOpenStatus: d.jobOpenStatus !== false,
          interviewRounds: Array.isArray(d.interviewRounds)
            ? d.interviewRounds.join(", ")
            : "",
          domainSkills: Array.isArray(d.domainSkills)
            ? d.domainSkills.join(", ")
            : "",
          softSkills: Array.isArray(d.softSkills)
            ? d.softSkills.join(", ")
            : "",
        });
      } catch (err) {
        if (cancelled) return;
        setDetailsError(
          err.response?.data?.message || "Failed to load job details.",
        );
      } finally {
        if (!cancelled) setLoadingDetails(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [job?.id]);

  // Probe JD existence — endpoint returns 404 if no JD is on the row.
  useEffect(() => {
    if (!job?.id) return;
    let cancelled = false;
    (async () => {
      setJdMeta({ loading: true, filename: null, available: false });
      try {
        const r = await axiosInstance.get(
          `/job-posting/job-details/${job.id}/jd-media/presigned-url?mode=inline`,
        );
        if (cancelled) return;
        if (r.data?.success && r.data?.url) {
          setJdMeta({
            loading: false,
            available: true,
            filename: parseFilenameFromCD(r.data.contentDisposition),
          });
        } else {
          setJdMeta({ loading: false, available: false, filename: null });
        }
      } catch {
        if (!cancelled)
          setJdMeta({ loading: false, available: false, filename: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [job?.id]);

  const splitList = (s) =>
    s
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

  const handleSave = async () => {
    if (loadingDetails) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("jobId", String(job.id));
      fd.append("positionName", form.positionName || "");
      splitList(form.locations).forEach((v) => fd.append("locations", v));
      fd.append("minCtc", form.minCtc || "");
      fd.append("maxCtc", form.maxCtc || "");
      fd.append("minimumExperience", form.minimumExperience || "");
      fd.append("maximumExperience", form.maximumExperience || "");
      fd.append(
        "positionCounts",
        form.positionCounts === "" ? "" : String(form.positionCounts),
      );
      fd.append("hiringType", form.hiringType || "");
      fd.append("jobOpenStatus", String(!!form.jobOpenStatus));
      splitList(form.interviewRounds).forEach((v) =>
        fd.append("interviewRounds", v),
      );
      splitList(form.domainSkills).forEach((v) => fd.append("domainSkills", v));
      splitList(form.softSkills).forEach((v) => fd.append("softSkills", v));

      // Round-trip the backend-only fields the Core form doesn't edit so they
      // aren't wiped (multipart save = full replace).
      const d = rawDetails || {};
      if (d.jobCreationStatus)
        fd.append("jobCreationStatus", d.jobCreationStatus);
      if (d.vacancyStartDate) fd.append("vacancyStartDate", d.vacancyStartDate);
      if (d.vacancyClosureDate)
        fd.append("vacancyClosureDate", d.vacancyClosureDate);
      if (d.vacancyApprovalDate)
        fd.append("vacancyApprovalDate", d.vacancyApprovalDate);
      if (d.closureTargetDate)
        fd.append("closureTargetDate", d.closureTargetDate);
      if (d.hiringOperations) fd.append("hiringOperations", d.hiringOperations);
      if (d.jdJson) fd.append("jdJson", d.jdJson);
      if (Array.isArray(d.jobDescription))
        d.jobDescription.forEach((v) => fd.append("jobDescription", v));
      if (Array.isArray(d.recruiterName))
        d.recruiterName.forEach((v) => fd.append("recruiterName", v));
      if (Array.isArray(d.interviewers))
        d.interviewers.forEach((v) => fd.append("interviewers", v));
      if (Array.isArray(d.placementAgencies))
        d.placementAgencies.forEach((v) => fd.append("placementAgencies", v));
      if (Array.isArray(d.openPositions))
        d.openPositions.forEach((p, i) => {
          if (p?.city != null)
            fd.append(`openPositions[${i}].city`, String(p.city));
          if (p?.openings != null)
            fd.append(`openPositions[${i}].openings`, String(p.openings));
        });

      const userId = localStorage.getItem("userId");
      if (userId) fd.append("userId", userId);

      if (pendingJdFile) fd.append("jobDescriptionMedia", pendingJdFile);

      const r = await axiosInstance.post(
        "/job-posting/job-details/save",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (r.data?.success === false) {
        throw new Error(r.data?.message || "Save failed.");
      }
      const d2 = r.data?.data || {};
      setRawDetails(d2);
      setEditing(false);
      setPendingJdFile(null);
      if (pendingJdFile) {
        // Refresh JD filename after a new upload.
        setJdMeta((m) => ({ ...m, available: true, filename: pendingJdFile.name }));
      }
      // Map back to the slim card-shape that the parent grid keeps.
      onUpdated &&
        onUpdated({
          ...job,
          id: d2.jobId ?? job.id,
          title: d2.positionName || job.title,
          status: d2.jobOpenStatus === false ? "CLOSED" : "OPEN",
          location: Array.isArray(d2.locations)
            ? d2.locations.join(", ")
            : job.location,
          openings: d2.positionCounts ?? job.openings,
          hiringType: d2.hiringType ?? job.hiringType,
          minExperience: d2.minimumExperience ?? job.minExperience,
          maxExperience: d2.maximumExperience ?? job.maxExperience,
          minCtc: d2.minCtc ?? job.minCtc,
          maxCtc: d2.maxCtc ?? job.maxCtc,
          interviewRounds: Array.isArray(d2.interviewRounds)
            ? d2.interviewRounds
            : job.interviewRounds,
          skills: Array.isArray(d2.domainSkills) ? d2.domainSkills : job.skills,
        });
    } catch (e) {
      console.error("Save error:", e);
      window.alert(
        e.response?.data?.message || e.message || "Failed to save job details.",
      );
    } finally {
      setSaving(false);
    }
  };

  const openJdPreview = async () => {
    try {
      const r = await axiosInstance.get(
        `/job-posting/job-details/${job.id}/jd-media/presigned-url?mode=inline`,
      );
      if (r.data?.success && r.data?.url) {
        window.open(r.data.url, "_blank", "noopener");
      } else {
        window.alert(r.data?.message || "No JD available for this job.");
      }
    } catch (err) {
      window.alert(
        err.response?.data?.message || "Failed to load JD preview.",
      );
    }
  };

  const downloadJdFile = async () => {
    try {
      const r = await axiosInstance.get(
        `/job-posting/job-details/${job.id}/jd-media/presigned-url?mode=attachment`,
      );
      if (r.data?.success && r.data?.url) {
        const a = document.createElement("a");
        a.href = r.data.url;
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        window.alert(r.data?.message || "No JD available for this job.");
      }
    } catch (err) {
      window.alert(err.response?.data?.message || "Failed to download JD.");
    }
  };

  // Candidate picker for invites
  const [showCandidatePicker, setShowCandidatePicker] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState({
    q: "",
    skill: "",
    location: "",
    minYears: "",
    maxYears: "",
  });
  const [candidateResults, setCandidateResults] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState({});
  const [sendingInvites, setSendingInvites] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const searchCandidates = async () => {
    setLoadingCandidates(true);
    try {
      const params = new URLSearchParams();
      params.append("size", "50");
      if (candidateSearch.q) params.append("q", candidateSearch.q);
      if (candidateSearch.skill) params.append("skill", candidateSearch.skill);
      if (candidateSearch.location)
        params.append("location", candidateSearch.location);
      if (candidateSearch.minYears)
        params.append("minYears", candidateSearch.minYears);
      if (candidateSearch.maxYears)
        params.append("maxYears", candidateSearch.maxYears);
      const r = await axiosInstance.get(`/ai/resumes?${params.toString()}`);
      const data = r.data?.content || r.data || [];
      setCandidateResults(Array.isArray(data) ? data : []);
    } catch {
      setCandidateResults([]);
    } finally {
      setLoadingCandidates(false);
    }
  };

  const toggleCandidate = (id) =>
    setSelectedCandidates((s) => ({ ...s, [id]: !s[id] }));
  const selectAllCandidates = () => {
    const all = {};
    candidateResults.forEach((c) => {
      if (c.candidateEmail) all[c.id] = true;
    });
    setSelectedCandidates(all);
  };
  const deselectAllCandidates = () => setSelectedCandidates({});

  const sendInvites = async () => {
    const ids = Object.keys(selectedCandidates).filter(
      (k) => selectedCandidates[k],
    );
    if (ids.length === 0) return;
    setSendingInvites(true);
    setInviteResult(null);
    try {
      const r = await axiosInstance.post(`/ai/jobs/${job.id}/send-invites`, {
        candidateIds: ids,
      });
      setInviteResult(r.data);
      setSelectedCandidates({});
    } catch (e) {
      setInviteResult({
        error: e.response?.data?.error || "Failed to send invites",
      });
    } finally {
      setSendingInvites(false);
    }
  };

  const selectedCount =
    Object.values(selectedCandidates).filter(Boolean).length;

  // Load attachments
  const loadAttachments = async () => {
    setLoadingAttachments(true);
    try {
      const r = await axiosInstance.get(`/ai/jobs/${job.id}/attachments`);
      setAttachments(r.data || []);
    } catch {
      setAttachments([]);
    } finally {
      setLoadingAttachments(false);
    }
  };
  // useEffect(() => {
  //   loadAttachments();
  // }, [job.id]);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      const r = await axiosInstance.post(`/ai/jobs/${job.id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments(r.data || []);
    } catch (e) {
      console.error("Upload error:", e);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const deleteAttachment = async (attId) => {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      await axiosInstance.delete(`/ai/jobs/${job.id}/attachments/${attId}`);
      setAttachments((prev) => prev.filter((a) => a.id !== attId));
    } catch (e) {
      console.error("Delete error:", e);
    }
  };

  const downloadAttachment = (att) => {
    window.open(`/ai/jobs/${job.id}/attachments/${att.id}/download`, "_blank");
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1048576).toFixed(1) + " MB";
  };

  const j = job;

  return (
    <div
      className="fixed inset-0 flex flex-col bg-vf-bg"
      style={{ zIndex: 9999 }}
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-6 py-4 bg-white"
        style={{ borderBottom: "0.5px solid rgba(99,102,241,0.15)" }}
      >
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="btn-ghost btn-sm flex items-center gap-1"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back to Jobs
          </button>
          <div>
            <h1 className="text-xl font-semibold text-vf-base">{j.title}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              {j.location && (
                <span className="text-xs text-vf-muted">{j.location}</span>
              )}
              {j.employmentType && (
                <span className="text-xs text-vf-muted">
                  · {j.employmentType}
                </span>
              )}
              <span
                className={`badge-${j.status === "OPEN" ? "success" : j.status === "PAUSED" ? "warning" : "danger"} ml-1`}
              >
                {j.status}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <button
                onClick={() => setEditing(false)}
                className="btn-ghost btn-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary btn-sm"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditing(true)}
              disabled={loadingDetails}
              className="btn-secondary btn-sm flex items-center gap-1 disabled:opacity-50"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto p-6 space-y-5">
          {/* Job Info */}
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-vf-base mb-4">
              Job Details
            </h2>
            {detailsError ? (
              <div className="text-sm text-vf-danger">{detailsError}</div>
            ) : loadingDetails ? (
              <div className="text-sm text-vf-muted">
                Loading job details…
              </div>
            ) : editing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="input-label">Position Name</label>
                  <input
                    className="input"
                    value={form.positionName}
                    onChange={(e) =>
                      handleChange("positionName", e.target.value)
                    }
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="input-label">
                    Locations (comma-separated)
                  </label>
                  <input
                    className="input"
                    value={form.locations}
                    onChange={(e) => handleChange("locations", e.target.value)}
                    placeholder="Bengaluru, Pune"
                  />
                </div>
                <div>
                  <label className="input-label">Min Experience (years)</label>
                  <input
                    className="input"
                    value={form.minimumExperience}
                    onChange={(e) =>
                      handleChange("minimumExperience", e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="input-label">Max Experience (years)</label>
                  <input
                    className="input"
                    value={form.maximumExperience}
                    onChange={(e) =>
                      handleChange("maximumExperience", e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="input-label">Min CTC (LPA)</label>
                  <input
                    className="input"
                    value={form.minCtc}
                    onChange={(e) => handleChange("minCtc", e.target.value)}
                  />
                </div>
                <div>
                  <label className="input-label">Max CTC (LPA)</label>
                  <input
                    className="input"
                    value={form.maxCtc}
                    onChange={(e) => handleChange("maxCtc", e.target.value)}
                  />
                </div>
                <div>
                  <label className="input-label">Position Count</label>
                  <input
                    className="input"
                    type="number"
                    value={form.positionCounts}
                    onChange={(e) =>
                      handleChange("positionCounts", e.target.value)
                    }
                  />
                </div>
                <div>
                  <label className="input-label">Hiring Type</label>
                  <select
                    className="select w-full"
                    value={form.hiringType}
                    onChange={(e) =>
                      handleChange("hiringType", e.target.value)
                    }
                  >
                    <option value="">Select…</option>
                    <option value="Lateral Hiring">Lateral Hiring</option>
                    <option value="Campus Hiring">Campus Hiring</option>
                  </select>
                </div>
                <div>
                  <label className="input-label">Status</label>
                  <select
                    className="select w-full"
                    value={form.jobOpenStatus ? "OPEN" : "CLOSED"}
                    onChange={(e) =>
                      handleChange("jobOpenStatus", e.target.value === "OPEN")
                    }
                  >
                    <option value="OPEN">OPEN</option>
                    <option value="CLOSED">CLOSED</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="input-label">
                    Interview Rounds (comma-separated)
                  </label>
                  <input
                    className="input"
                    value={form.interviewRounds}
                    onChange={(e) =>
                      handleChange("interviewRounds", e.target.value)
                    }
                    placeholder="HR, Tech, Manager"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="input-label">
                    Domain Skills (comma-separated)
                  </label>
                  <input
                    className="input"
                    value={form.domainSkills}
                    onChange={(e) =>
                      handleChange("domainSkills", e.target.value)
                    }
                    placeholder="Java, Spring Boot"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="input-label">
                    Soft Skills (comma-separated)
                  </label>
                  <input
                    className="input"
                    value={form.softSkills}
                    onChange={(e) =>
                      handleChange("softSkills", e.target.value)
                    }
                    placeholder="Communication, Leadership"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
                  <div>
                    <div className="text-xs text-vf-muted">Position</div>
                    <div className="text-sm font-medium text-vf-base mt-0.5">
                      {form.positionName || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-vf-muted">Location(s)</div>
                    <div className="text-sm font-medium text-vf-base mt-0.5">
                      {form.locations || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-vf-muted">Experience</div>
                    <div className="text-sm font-medium text-vf-base mt-0.5">
                      {form.minimumExperience || form.maximumExperience
                        ? `${form.minimumExperience || 0}–${form.maximumExperience || "?"} yrs`
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-vf-muted">CTC (LPA)</div>
                    <div className="text-sm font-medium text-vf-base mt-0.5">
                      {form.minCtc || form.maxCtc
                        ? `${form.minCtc || "—"} – ${form.maxCtc || "—"}`
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-vf-muted">Openings</div>
                    <div className="text-sm font-medium text-vf-base mt-0.5">
                      {form.positionCounts || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-vf-muted">Hiring Type</div>
                    <div className="text-sm font-medium text-vf-base mt-0.5">
                      {form.hiringType || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-vf-muted">Status</div>
                    <div className="mt-0.5">
                      <span
                        className={`badge-${form.jobOpenStatus ? "success" : "danger"}`}
                      >
                        {form.jobOpenStatus ? "OPEN" : "CLOSED"}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-vf-muted">Interview Rounds</div>
                    <div className="text-sm font-medium text-vf-base mt-0.5">
                      {form.interviewRounds || "—"}
                    </div>
                  </div>
                </div>
                {(form.domainSkills || form.softSkills) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    {form.domainSkills && (
                      <div>
                        <div className="text-xs text-vf-muted">
                          Domain Skills
                        </div>
                        <div className="text-sm font-medium text-vf-base mt-0.5">
                          {form.domainSkills}
                        </div>
                      </div>
                    )}
                    {form.softSkills && (
                      <div>
                        <div className="text-xs text-vf-muted">Soft Skills</div>
                        <div className="text-sm font-medium text-vf-base mt-0.5">
                          {form.softSkills}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* JD Document */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-vf-base">
                Job Description (JD)
              </h2>
              <div className="flex items-center gap-2">
                {jdMeta.available && (
                  <>
                    <button
                      onClick={openJdPreview}
                      className="btn-ghost btn-sm"
                    >
                      Preview
                    </button>
                    <button
                      onClick={downloadJdFile}
                      className="btn-ghost btn-sm"
                    >
                      Download
                    </button>
                  </>
                )}
                {editing && (
                  <>
                    <input
                      ref={jdFileInputRef}
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) =>
                        setPendingJdFile(e.target.files?.[0] || null)
                      }
                    />
                    <button
                      onClick={() => jdFileInputRef.current?.click()}
                      className="btn-secondary btn-sm"
                    >
                      {jdMeta.available ? "Replace JD" : "Upload JD"}
                    </button>
                  </>
                )}
              </div>
            </div>
            {jdMeta.loading ? (
              <div className="text-xs text-vf-muted">Checking JD…</div>
            ) : jdMeta.available ? (
              <div className="text-xs text-vf-muted">
                {jdMeta.filename || "JD attached"}
                {pendingJdFile && (
                  <span className="ml-2 text-vf-accent">
                    · New file: {pendingJdFile.name} (uploads on Save)
                  </span>
                )}
              </div>
            ) : (
              <div className="text-xs text-vf-muted">
                No JD uploaded.
                {pendingJdFile && (
                  <span className="ml-2 text-vf-accent">
                    · {pendingJdFile.name} selected (uploads on Save)
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Pipeline Stats */}
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-vf-base mb-3">
              Pipeline
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="stat-card text-center">
                <div className="stat-label">Invited</div>
                <div className="stat-value">{j.invitedCount || 0}</div>
              </div>
              <div className="stat-card text-center">
                <div className="stat-label">In Progress</div>
                <div className="stat-value">{j.inProgressCount || 0}</div>
              </div>
              <div className="stat-card text-center">
                <div className="stat-label">Completed</div>
                <div className="stat-value">{j.completedCount || 0}</div>
              </div>
              <div className="stat-card text-center">
                <div className="stat-label">Scored</div>
                <div className="stat-value">{j.scoredCount || 0}</div>
              </div>
              <div className="stat-card text-center">
                <div className="stat-label">Avg Score</div>
                <div className="stat-value">
                  {j.avgScore != null ? `${Math.round(j.avgScore)}` : "—"}
                </div>
              </div>
            </div>
          </div>

          {/* Attachments */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-vf-base">
                Attachments{" "}
                {attachments.length > 0 && (
                  <span className="text-vf-muted font-normal">
                    ({attachments.length})
                  </span>
                )}
              </h2>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => uploadFiles(e.target.files)}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="btn-secondary btn-sm flex items-center gap-1.5"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  {uploading ? "Uploading..." : "Upload Files"}
                </button>
              </div>
            </div>

            {loadingAttachments ? (
              <div className="py-4 text-center">
                <div className="skeleton h-4 w-24 mx-auto rounded" />
              </div>
            ) : attachments.length === 0 ? (
              <div className="py-6 text-center text-sm text-vf-muted">
                No attachments yet. Upload JD, scorecards, or any relevant
                documents.
              </div>
            ) : (
              <div className="space-y-2">
                {attachments.map((att) => (
                  <div
                    key={att.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-vf-tint group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-vf-primary/10 text-vf-primary flex items-center justify-center flex-shrink-0">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-vf-base truncate">
                          {att.fileName}
                        </div>
                        <div className="text-xs text-vf-muted">
                          {formatFileSize(att.fileSize)}
                          {att.uploadedBy && <span> · {att.uploadedBy}</span>}
                          {att.createdAt && (
                            <span>
                              {" "}
                              · {new Date(att.createdAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => downloadAttachment(att)}
                        className="btn-ghost btn-sm"
                        title="Download"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                      </button>
                      <button
                        onClick={() => deleteAttachment(att.id)}
                        className="btn-ghost btn-sm text-vf-danger"
                        title="Delete"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Send Invites — Candidate Picker */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-vf-base">
                Send Interview Invites
              </h2>
              <button
                onClick={() => {
                  setShowCandidatePicker(!showCandidatePicker);
                  if (!showCandidatePicker) searchCandidates();
                }}
                className="btn-primary btn-sm"
              >
                {showCandidatePicker ? "Close" : "Select Candidates"}
              </button>
            </div>

            {/* Invite result */}
            {inviteResult && (
              <div
                className={`rounded-xl p-4 text-sm mb-4 ${inviteResult.error ? "bg-red-50 text-red-600" : "bg-green-50 text-green-700"}`}
              >
                {inviteResult.error ? (
                  inviteResult.error
                ) : (
                  <>
                    Sent: <strong>{inviteResult.sent}</strong> | Skipped:{" "}
                    <strong>{inviteResult.skipped}</strong> | Failed:{" "}
                    <strong>{inviteResult.failed}</strong>
                  </>
                )}
              </div>
            )}

            {showCandidatePicker && (
              <div className="space-y-4">
                {/* Search filters */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <div>
                    <input
                      className="input text-xs"
                      placeholder="Search name, email, skill..."
                      value={candidateSearch.q}
                      onChange={(e) =>
                        setCandidateSearch({
                          ...candidateSearch,
                          q: e.target.value,
                        })
                      }
                      onKeyDown={(e) => e.key === "Enter" && searchCandidates()}
                    />
                  </div>
                  <div>
                    <input
                      className="input text-xs"
                      placeholder="Skill"
                      value={candidateSearch.skill}
                      onChange={(e) =>
                        setCandidateSearch({
                          ...candidateSearch,
                          skill: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <input
                      className="input text-xs"
                      placeholder="Location"
                      value={candidateSearch.location}
                      onChange={(e) =>
                        setCandidateSearch({
                          ...candidateSearch,
                          location: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <input
                      className="input text-xs"
                      type="number"
                      placeholder="Min yrs"
                      value={candidateSearch.minYears}
                      onChange={(e) =>
                        setCandidateSearch({
                          ...candidateSearch,
                          minYears: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <button
                      onClick={searchCandidates}
                      className="btn-secondary btn-sm w-full"
                    >
                      {loadingCandidates ? "Searching..." : "Search"}
                    </button>
                  </div>
                </div>

                {/* Results table */}
                {candidateResults.length > 0 && (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-vf-muted">
                        {candidateResults.length} candidates found ·{" "}
                        {selectedCount} selected
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={selectAllCandidates}
                          className="btn-ghost btn-sm text-xs"
                        >
                          Select All
                        </button>
                        <button
                          onClick={deselectAllCandidates}
                          className="btn-ghost btn-sm text-xs"
                        >
                          Deselect All
                        </button>
                      </div>
                    </div>
                    <div
                      className="max-h-64 overflow-y-auto rounded-lg"
                      style={{ border: "0.5px solid rgba(99,102,241,0.15)" }}
                    >
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="tbl-header">
                            <th className="px-3 py-2 w-8"></th>
                            <th className="text-left px-3 py-2">Name</th>
                            <th className="text-left px-3 py-2">Role</th>
                            <th className="text-left px-3 py-2">Exp</th>
                            <th className="text-left px-3 py-2">Location</th>
                            <th className="text-left px-3 py-2">Email</th>
                          </tr>
                        </thead>
                        <tbody>
                          {candidateResults.map((c) => (
                            <tr
                              key={c.id}
                              className={`tbl-row cursor-pointer ${selectedCandidates[c.id] ? "bg-vf-tint" : ""}`}
                              onClick={() =>
                                c.candidateEmail && toggleCandidate(c.id)
                              }
                            >
                              <td className="px-3 py-2">
                                <input
                                  type="checkbox"
                                  checked={!!selectedCandidates[c.id]}
                                  onChange={() => toggleCandidate(c.id)}
                                  disabled={!c.candidateEmail}
                                />
                              </td>
                              <td className="px-3 py-2 font-medium text-vf-base">
                                {c.candidateName || "—"}
                              </td>
                              <td className="px-3 py-2 text-vf-muted">
                                {c.currentRole || "—"}
                              </td>
                              <td className="px-3 py-2 text-vf-muted">
                                {c.totalYearsExperience != null
                                  ? `${c.totalYearsExperience}y`
                                  : "—"}
                              </td>
                              <td className="px-3 py-2 text-vf-muted">
                                {c.currentLocation || "—"}
                              </td>
                              <td className="px-3 py-2 text-vf-muted">
                                {c.candidateEmail || (
                                  <span className="text-vf-danger">
                                    No email
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Send button */}
                    <div className="flex items-center justify-between pt-2">
                      <div className="text-xs text-vf-muted">
                        {selectedCount > 0
                          ? `${selectedCount} candidate${selectedCount > 1 ? "s" : ""} will receive an invitation email`
                          : "Select candidates to send invites"}
                      </div>
                      <button
                        onClick={sendInvites}
                        disabled={selectedCount === 0 || sendingInvites}
                        className="btn-primary btn-sm flex items-center gap-2 disabled:opacity-40"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M22 2L11 13" />
                          <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                        </svg>
                        {sendingInvites
                          ? "Sending..."
                          : `Send Invites (${selectedCount})`}
                      </button>
                    </div>
                  </>
                )}

                {!loadingCandidates && candidateResults.length === 0 && (
                  <div className="text-center py-6 text-sm text-vf-muted">
                    Search your resume database to find candidates for this job.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── JdPreviewModal ── */
// Renders the presigned inline URL in an <iframe>. Browsers handle PDF and image
// preview natively. We do not cache the URL — it expires (S3 returns 403 after
// expiresAt), so every open issues a fresh request.
const parseFilenameFromCD = (cd) => {
  if (!cd) return null;
  // RFC 5987 filename*=UTF-8''… takes priority over filename=…
  const ext = cd.match(/filename\*\s*=\s*(?:UTF-8'')?["']?([^";]+)["']?/i);
  if (ext) {
    try {
      return decodeURIComponent(ext[1]);
    } catch {
      return ext[1];
    }
  }
  const m = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
  return m ? m[1] : null;
};

const JdPreviewModal = ({ data, onClose, onDownload }) => {
  const { job, url, loading, error, contentDisposition } = data;
  const filename = parseFilenameFromCD(contentDisposition);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "92vw",
          maxWidth: 960,
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          background: "white",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 24px 48px rgba(15,23,42,0.18)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "14px 20px",
            borderBottom: "1px solid #E2E8F0",
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: "#0F172A",
                margin: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              JD &middot; {job?.title || "Untitled"}
            </h2>
            {filename && (
              <p
                style={{
                  fontSize: 12,
                  color: "#64748B",
                  margin: "2px 0 0",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {filename}
              </p>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              onClick={onDownload}
              disabled={!url || loading}
              style={{
                fontSize: 12,
                padding: "6px 14px",
                borderRadius: 6,
                border: "1px solid #C7D2FE",
                background: !url || loading ? "#EEF2FF" : "#EEF2FF",
                cursor: !url || loading ? "not-allowed" : "pointer",
                color: "#6A4DB3",
                fontWeight: 600,
                opacity: !url || loading ? 0.5 : 1,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download
            </button>
            <button
              onClick={onClose}
              aria-label="Close preview"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 22,
                color: "#94A3B8",
                lineHeight: 1,
                padding: "0 4px",
              }}
            >
              &times;
            </button>
          </div>
        </div>
        <div
          style={{
            flex: 1,
            background: "#F8FAFC",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {loading ? (
            <div style={{ textAlign: "center" }}>
              <div
                className="skeleton"
                style={{
                  height: 8,
                  width: 180,
                  borderRadius: 6,
                  margin: "0 auto",
                }}
              />
              <p style={{ fontSize: 12, color: "#64748B", marginTop: 12 }}>
                Loading JD preview…
              </p>
            </div>
          ) : error ? (
            <div style={{ textAlign: "center", padding: 24, maxWidth: 360 }}>
              <p
                style={{
                  fontSize: 14,
                  color: "#DC2626",
                  marginBottom: 6,
                  fontWeight: 500,
                }}
              >
                {error}
              </p>
              <p style={{ fontSize: 12, color: "#64748B", margin: 0 }}>
                Upload a JD for this job, then try again.
              </p>
            </div>
          ) : url ? (
            <iframe
              src={url}
              title={filename || "JD Preview"}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                background: "white",
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Jobs;
